import React, { useMemo } from 'react';
import { Select, SelectOption, TextInput, Toggle } from '@spoiler-alert/ui-library';
import { createUseStyles } from 'react-jss';
import { propTypes, styles } from './site-editing-helpers';
import { pricingStrategyAnchor, siteTags, subscriptionTiers } from '../../../utilities';
import EmailList from '../../../components/email-list/email-list';

const useStyles = createUseStyles(styles);

const FiscalYearMap = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const BasicDetails = ({ site, setFieldValue }) => {
  const classes = useStyles();

  const selectedRoles = useMemo(() => {
    return site.siteRole.map((role) => ({ value: role, text: role.replace('_', ' ') }));
  }, [site.siteRole]);

  const selectedSiteType = useMemo(() => {
    return { value: site.siteType, text: site.siteType.replace('_', ' ') };
  }, [site.siteType]);

  const selectedFiscalYearType = useMemo(() => {
    return { value: site.fiscalYearStart, text: FiscalYearMap[site.fiscalYearStart] };
  }, [site.fiscalYearStart]);

  const selectedSubscriptionTier = useMemo(() => {
    if (site.subscriptionTier) return { value: site.subscriptionTier, text: site.subscriptionTier };
    return { value: null, text: 'None' };
  }, [site.subscriptionTier]);

  const selectedTags = useMemo(() => {
    if (site.tags) return site.tags?.map((tag) => ({ value: tag, text: tag.replace('_', ' ') }));
    return [];
  }, [site.tags]);

  const selectedAnchor = useMemo(() => {
    if (site.defaultPricingStrategy)
      return { value: site.defaultPricingStrategy, text: pricingStrategyAnchor.get(site.defaultPricingStrategy) };
    return { value: null, text: 'None' };
  }, [site.defaultPricingStrategy]);

  const selectedBestByDateType = useMemo(() => {
    if (site.bestByDateType) return { value: site.bestByDateType, text: site.bestByDateType };
    return { value: null, text: 'None' };
  }, [site.bestByDateType]);

  return (
    <div className={classes.box}>
      <div className={classes.half}>
        <Select
          label="Site Role"
          multiple
          selectedItems={selectedRoles}
          onChange={setFieldValue.bind(this, 'select-multiple', 'siteRole')}
        >
          <SelectOption value="BUYER">BUYER</SelectOption>
          <SelectOption value="SELLER">SELLER</SelectOption>
          <SelectOption value="DONATION_SELLER">DONATION SELLER</SelectOption>
        </Select>
      </div>
      <div className={classes.half}>
        <Select
          label="Site Type"
          selectedItem={selectedSiteType}
          onChange={setFieldValue.bind(this, 'select', 'siteType')}
        >
          <SelectOption value="ORGANIZATION">ORGANIZATION</SelectOption>
          <SelectOption value="BUSINESS_UNIT">BUSINESS UNIT</SelectOption>
          <SelectOption value="FACILITY">FACILITY</SelectOption>
          <SelectOption value="STOREFRONT">STOREFRONT</SelectOption>
        </Select>
      </div>
      {site.siteType !== 'ORGANIZATION' && (
        <>
          <div className={classes.half}>
            <TextInput
              value={site.siteName}
              labelText="Site Name"
              disabled={site.siteType === 'ORGANIZATION'}
              onChange={setFieldValue.bind(this, 'text', 'siteName')}
            />
          </div>
          {site.siteRole.includes('SELLER') && (
            <div className={classes.half}>
              <TextInput
                value={site.itemImportSite || ''}
                labelText="Import Site Name"
                disabled={site.siteType === 'ORGANIZATION'}
                onChange={setFieldValue.bind(this, 'text', 'itemImportSite')}
              />
            </div>
          )}
        </>
      )}
      {site.siteType === 'ORGANIZATION' && (
        <>
          {site.siteRole.includes('SELLER') && (
            <div className={classes.half}>
              <Select
                label="Fiscal Year Start"
                selectedItem={selectedFiscalYearType}
                onChange={setFieldValue.bind(this, 'select', 'fiscalYearStart')}
              >
                {FiscalYearMap.map((label, value) => (
                  <SelectOption key={value} value={value}>
                    {label}
                  </SelectOption>
                ))}
              </Select>
            </div>
          )}

          {site.siteRole.includes('SELLER') && (
            <div className={classes.half}>
              <Select
                label="Subscription Tier"
                selectedItem={selectedSubscriptionTier}
                onChange={setFieldValue.bind(this, 'select', 'subscriptionTier')}
              >
                {[
                  <SelectOption key="none" value={null}>
                    None
                  </SelectOption>,
                  ...subscriptionTiers.map((label) => (
                    <SelectOption key={label} value={label}>
                      {label}
                    </SelectOption>
                  )),
                ]}
              </Select>
            </div>
          )}
          <div className={classes.half}>
            <Select
              label="Tags"
              multiple
              selectedItems={selectedTags}
              onChange={setFieldValue.bind(this, 'select-multiple', 'tags')}
            >
              {siteTags.map((tag) => (
                <SelectOption checkbox key={tag} value={tag}>
                  {tag.replace('_', ' ')}
                </SelectOption>
              ))}
            </Select>
          </div>
          {site.siteRole.includes('SELLER') && (
            <div className={classes.halfWrap}>
              <div className={classes.half}>
                <Select
                  label="Suggested Price Anchor"
                  onChange={setFieldValue.bind(this, 'select', 'defaultPricingStrategy')}
                  selectedItem={selectedAnchor}
                >
                  {Array.from(pricingStrategyAnchor.keys()).map((k) => (
                    <SelectOption key={k} value={k}>
                      {pricingStrategyAnchor.get(k)}
                    </SelectOption>
                  ))}
                </Select>
              </div>
              <div className={classes.half}>
                <TextInput
                  type="number"
                  min={0}
                  step={0.01}
                  value={site.defaultPricingPercentage}
                  onChange={setFieldValue.bind(this, 'text', 'defaultPricingPercentage')}
                  labelText="Suggested Price Anchor Modifier"
                />
              </div>
            </div>
          )}
          {site.siteRole.includes('SELLER') && (
            <div className={classes.half}>
              <Select
                label="Best By Date Type"
                selectedItem={selectedBestByDateType}
                onChange={setFieldValue.bind(this, 'select', 'bestByDateType')}
              >
                <SelectOption value="ALWAYS PROVIDED">ALWAYS PROVIDED</SelectOption>
                <SelectOption value="PARTIALLY PROVIDED">PARTIALLY PROVIDED</SelectOption>
                <SelectOption value="NOT PROVIDED">NOT PROVIDED</SelectOption>
              </Select>
            </div>
          )}
          {site.siteRole.includes('SELLER') && (
            <div>
              <EmailList
                label="Item Import Email Recipients"
                list={site.itemImportEmailRecipients}
                onChange={setFieldValue.bind(this, 'text[]', 'itemImportEmailRecipients')}
              />
            </div>
          )}
          {site.siteRole.includes('SELLER') && (
            <div>
              <EmailList
                label="Inventory Import Email Recipients"
                list={site.inventoryImportEmailRecipients}
                onChange={setFieldValue.bind(this, 'text[]', 'inventoryImportEmailRecipients')}
              />
            </div>
          )}
          {site.siteRole.includes('SELLER') && (
            <div className={classes.half}>
              <TextInput
                value={site.awardSheetEmailTemplate}
                labelText="Award Sheet Email Template"
                onChange={setFieldValue.bind(this, 'text', 'awardSheetEmailTemplate')}
              />
            </div>
          )}
        </>
      )}
      <div className={classes.half}>
        <Toggle
          labelText="Archived"
          value={site.archived}
          onClick={setFieldValue.bind(this, 'toggle', 'archived', !site.archived)}
        />
      </div>
    </div>
  );
};

BasicDetails.propTypes = propTypes;

export default BasicDetails;
